// 材料管理(受払簿) — 運用管理系
// 材料ごとの納入・払出(消費)・残数を時系列で。サマリシート + 材料別シート。

import ExcelJS from "exceljs";
import type { ExtendedDB } from "../../../../types/extendedDB";
import { buildLookups, downloadBlob, todayStr, setHeaderRow, fmtDate, sumContentKg, uniqueLotIds , processAreaIdOf } from "./helpers";

function resolveWork(db: ExtendedDB, lk: ReturnType<typeof buildLookups>, workId?: number) {
  if (workId != null) {
    return lk.workById.get(workId);
  }
  return db.works.find((w) => w.deleted_at == null);
}

type Material = ExtendedDB["materials"][number];

function pickRelevantMaterials(baseMaterials: Material[], relevantMaterialIds: Set<number>): Material[] {
  if (relevantMaterialIds.size > 0) {
    return baseMaterials.filter((m) => relevantMaterialIds.has(m.id));
  }
  return baseMaterials;
}

function resolvePlanArea(lk: ReturnType<typeof buildLookups>, areaId?: string) {
  if (areaId) {
    return lk.planAreaById.get(areaId);
  }
  return undefined;
}

// 消費先 Construction が対象工事に属するかを判定する。対象工事未指定(work=undefined)時は常に true。
function constructionBelongsToWork(
  lk: ReturnType<typeof buildLookups>,
  work: ReturnType<typeof resolveWork>,
  constructionId: string,
): boolean {
  if (!work) {
    return true;
  }
  const processes = lk.processesByConstructionId.get(constructionId) ?? [];
  const areaId = processAreaIdOf(processes[0]);
  const area = resolvePlanArea(lk, areaId);
  return area?.work_id === work.id;
}

function lookupListById<K, V>(map: Map<K, V[]>, key: K | undefined | null): V[] {
  if (key == null) {
    return [];
  }
  return map.get(key) ?? [];
}

// 小数個(使用量・残数量)を「N個」表記用に整形する。
function fmtQty(n: number): string {
  return (Math.round(n * 100) / 100).toString();
}

export async function exportMaterialLedger(db: ExtendedDB, workId?: number): Promise<void> {
  const lk = buildLookups(db);
  const work = resolveWork(db, lk, workId);

  const wb = new ExcelJS.Workbook();
  wb.creator = "奥村組 トレーサビリティシステム";

  const relevantMaterialIds = new Set<number>();
  for (const c of db.carryIns.filter((c) => c.deleted_at == null)) {
    if (work && c.work_id !== work.id) continue;
    const qrs = db.materialQrs.filter((q) => q.carry_in_id === c.id);
    for (const q of qrs) {
      if (!q.lot_id) continue;
      const lot = lk.lotById.get(q.lot_id);
      if (lot && lot.material_id != null) relevantMaterialIds.add(lot.material_id);
    }
  }
  const baseMaterials = db.materials.filter((m) => m.deleted_at == null && m.qr_managed);
  const materials = pickRelevantMaterials(baseMaterials, relevantMaterialIds);

  if (materials.length === 0) {
    const wsEmpty = wb.addWorksheet("受払簿");
    wsEmpty.getCell("A1").value = "※ QR管理材料が存在しません";
    const bufEmpty = await wb.xlsx.writeBuffer();
    downloadBlob(bufEmpty, `材料管理_${todayStr()}.xlsx`);
    return;
  }

  const wsSummary = wb.addWorksheet("受払簿サマリ");
  wsSummary.getCell("A1").value = "納品数量一覧表";
  wsSummary.getCell("A1").font = { bold: true, size: 14 };
  if (work) {
    wsSummary.getCell("A2").value = "工事:";
    wsSummary.getCell("B2").value = work.official_name;
  }
  wsSummary.getCell("A3").value = "出力日:";
  wsSummary.getCell("B3").value = todayStr();
  const summaryHeaders: Array<[string, number]> = [
    ["番号", 8], ["材料名", 26], ["納入数量", 12], ["納入数量(累計)", 14],
    ["使用数量", 12], ["使用数量(累計)", 14], ["残数量", 12], ["備考", 30],
  ];
  const summaryHeaderRow = 5;
  summaryHeaders.forEach((h, i) => {
    wsSummary.getCell(summaryHeaderRow, i + 1).value = h[0];
    wsSummary.getColumn(i + 1).width = h[1];
  });
  setHeaderRow(wsSummary, summaryHeaderRow);

  let summaryR = summaryHeaderRow + 1;
  materials.forEach((m, idx) => {
    const materialDetails = lk.materialDetailsByMaterialId.get(m.id) ?? [];
    const contentKgs = sumContentKg(materialDetails);

    const qrsForMaterial = db.materialQrs.filter((q) => {
      if (!q.lot_id) return false;
      const lt = lk.lotById.get(q.lot_id);
      return lt?.material_id === m.id;
    });

    const carriedInQrs = qrsForMaterial.filter((q) => {
      if (!q.carry_in_id) return false;
      if (!work) return true;
      const ci = lk.carryInById.get(q.carry_in_id);
      return ci?.work_id === work.id;
    });
    // QR部分消費モデル: 使用数量 = 対象QRの、対象工事に属するエントリのみの Σ usedRatio(小数個)。
    // 同一QRが複数工事に跨って部分消費されていても、他工事分は加算しない(工事跨ぎの過大計上防止)。
    // QRごとに Σ を [0,1] へ clamp してから合算する(StockPage.buildRatioByQrId と同じ正規化)。
    // ConstructionMaterialConsumption が1件も無い QR は legacy(旧データ)消費判定にフォールバックする。
    // 詳細シート(施工別イベント)は施工の合算値が qty<=1e-3 なら微小消費としてスキップするため、
    // サマリの使用数量も同じ粒度(施工単位)で合算してから同じ閾値で切り捨て、両シートを一致させる。
    const consumedQtyByConstructionId = new Map<string, number>();
    for (const q of qrsForMaterial) {
      const entries = lookupListById(lk.consumptionsByQrId, q.id);
      if (entries.length > 0) {
        const workEntries = entries.filter((e) => constructionBelongsToWork(lk, work, e.constructionId));
        if (workEntries.length > 0) {
          const rawTotal = workEntries.reduce((a, e) => a + e.usedRatio, 0);
          const clampedTotal = Math.min(1, Math.max(0, rawTotal));
          const scale = rawTotal > 0 ? clampedTotal / rawTotal : 0;
          for (const e of workEntries) {
            consumedQtyByConstructionId.set(e.constructionId, (consumedQtyByConstructionId.get(e.constructionId) ?? 0) + e.usedRatio * scale);
          }
        }
        continue;
      }
    }
    let consumedQty = 0;
    for (const qtyForConstruction of consumedQtyByConstructionId.values()) {
      if (qtyForConstruction > 1e-3) {
        consumedQty += qtyForConstruction;
      }
    }
    const carriedInCount = carriedInQrs.length;
    const remaining = carriedInCount - consumedQty;

    const carriedInKg = carriedInCount * contentKgs;
    const consumedKg = consumedQty * contentKgs;
    const remainingKg = remaining * contentKgs;

    wsSummary.getCell(summaryR, 1).value = idx + 1;
    wsSummary.getCell(summaryR, 2).value = m.name;
    wsSummary.getCell(summaryR, 3).value = `${carriedInCount}個 / ${carriedInKg.toFixed(1)}kg`;
    wsSummary.getCell(summaryR, 4).value = `${carriedInCount}個 / ${carriedInKg.toFixed(1)}kg`;
    wsSummary.getCell(summaryR, 5).value = `${fmtQty(consumedQty)}個 / ${consumedKg.toFixed(1)}kg`;
    wsSummary.getCell(summaryR, 6).value = `${fmtQty(consumedQty)}個 / ${consumedKg.toFixed(1)}kg`;
    wsSummary.getCell(summaryR, 7).value = `${fmtQty(remaining)}個 / ${remainingKg.toFixed(1)}kg`;
    wsSummary.getCell(summaryR, 8).value = "";
    summaryR++;
  });

  // 材料毎の詳細シート
  materials.forEach((m) => {
    const sheetName = m.name.replace(/[\\/*?:[\]]/g, "_").slice(0, 31);
    const ws = wb.addWorksheet(sheetName);
    ws.getCell("A1").value = `受払簿 — ${m.name}`;
    ws.getCell("A1").font = { bold: true, size: 12 };
    if (work) {
      ws.getCell("A2").value = "工事:";
      ws.getCell("B2").value = work.official_name;
    }
    const headers: Array<[string, number]> = [
      ["番号", 8], ["日付", 12], ["イベント", 14], ["Lot/搬入ID", 18], ["LotNo", 14],
      ["納入数量", 12], ["使用数量", 12], ["残数量", 12], ["備考", 30],
    ];
    const headerRow = 4;
    headers.forEach((h, i) => {
      ws.getCell(headerRow, i + 1).value = h[0];
      ws.getColumn(i + 1).width = h[1];
    });
    setHeaderRow(ws, headerRow);

    type Event = { date: string; kind: "搬入" | "消費"; lotId?: string; lotNo: string; refId: string; count: number; remarks: string };
    const events: Event[] = [];

    const carryInsByThisMaterial = new Set<string>();
    db.materialQrs.forEach((q) => {
      if (!q.lot_id || !q.carry_in_id) return;
      const lt = lk.lotById.get(q.lot_id);
      if (lt?.material_id !== m.id) return;
      carryInsByThisMaterial.add(q.carry_in_id);
    });

    for (const ciId of carryInsByThisMaterial) {
      const carryIn = lk.carryInById.get(ciId);
      if (!carryIn) continue;
      if (work && carryIn.work_id !== work.id) continue;
      const qrs2 = db.materialQrs.filter((q) => q.carry_in_id === ciId && q.lot_id != null && lk.lotById.get(q.lot_id)?.material_id === m.id);
      const lotIds = uniqueLotIds(qrs2);
      for (const lotId of lotIds) {
        const lt2 = lk.lotById.get(lotId);
        const lotQrs = qrs2.filter((q) => q.lot_id === lotId);
        events.push({
          date: fmtDate(carryIn.received_at),
          kind: "搬入",
          lotId,
          lotNo: lt2?.lot_no ?? "",
          refId: ciId.slice(0, 8),
          count: lotQrs.length,
          remarks: carryIn.remarks ?? "",
        });
      }
    }

    // QR部分消費モデル: 消費イベントの数量 = その施工の ConstructionMaterialConsumption(この材料分のみ、かつ対象工事に属する分のみ) Σ usedRatio(小数個)。
    //   他工事に属する消費は出さない(サマリの使用数量と同じスコープ・同じ式に揃える)。
    //   0kg(紐付けのみ)は数量が動かないため受払簿には出さない。
    //   QRごとに、対象工事分の Σ を [0,1] へ clamp し(StockPage.buildRatioByQrId と同じ正規化)、超過分は
    //   各施工の按分比を保ったまま比例縮小する。ConstructionMaterialConsumption が1件も無い QR は legacy(旧データ)消費判定にフォールバックする。
    const qtyByConstructionId = new Map<string, number>();
    db.materialQrs.forEach((q) => {
      if (!q.lot_id) return;
      const lt3 = lk.lotById.get(q.lot_id);
      if (lt3?.material_id !== m.id) return;
      const entries = lookupListById(lk.consumptionsByQrId, q.id);
      if (entries.length > 0) {
        const workEntries = entries.filter((e) => constructionBelongsToWork(lk, work, e.constructionId));
        if (workEntries.length > 0) {
          const rawTotal = workEntries.reduce((a, e) => a + e.usedRatio, 0);
          const clampedTotal = Math.min(1, Math.max(0, rawTotal));
          const scale = rawTotal > 0 ? clampedTotal / rawTotal : 0;
          for (const e of workEntries) {
            qtyByConstructionId.set(e.constructionId, (qtyByConstructionId.get(e.constructionId) ?? 0) + e.usedRatio * scale);
          }
        }
        return;
      }
    });
    for (const [cId, qtyForMat] of qtyByConstructionId) {
      const construction = lk.constructionById.get(cId);
      if (!construction) continue;
      if (qtyForMat <= 1e-3) continue;
      events.push({
        date: fmtDate(construction.executed_at),
        kind: "消費",
        refId: cId.slice(0, 8),
        lotNo: "",
        count: qtyForMat,
        remarks: construction.remarks ?? "",
      });
    }

    events.sort((a, b) => a.date.localeCompare(b.date));

    let runningBalance = 0;
    let row = headerRow + 1;
    events.forEach((ev, idx) => {
      ws.getCell(row, 1).value = idx + 1;
      ws.getCell(row, 2).value = ev.date;
      ws.getCell(row, 3).value = ev.kind;
      ws.getCell(row, 4).value = ev.refId;
      ws.getCell(row, 5).value = ev.lotNo;
      if (ev.kind === "搬入") {
        ws.getCell(row, 6).value = Math.round(ev.count * 100) / 100;
        runningBalance += ev.count;
      } else {
        ws.getCell(row, 7).value = Math.round(ev.count * 100) / 100;
        runningBalance -= ev.count;
      }
      ws.getCell(row, 8).value = Math.round(runningBalance * 100) / 100;
      ws.getCell(row, 9).value = ev.remarks;
      row++;
    });

    if (events.length === 0) {
      ws.getCell(headerRow + 1, 1).value = "※ この材料の搬入・消費イベントが存在しません";
      ws.getCell(headerRow + 1, 1).font = { italic: true };
    }
  });

  const buffer = await wb.xlsx.writeBuffer();
  downloadBlob(buffer, `材料管理_${todayStr()}.xlsx`);
}
